import {
	Button,
	PlusIcon,
	useUrlParams,
} from '@aleph/shared-components';
import {
	addSupergridPropsToData, getLevelByDepth, getLevelDepth, useSupergridMutations,
} from '@aleph/supergrid';
import { useQuery } from '@apollo/client';
import { startCase } from 'lodash';
import {
	generateMutationHash,
	getSpotifyParentLevel,
	SPOTIFY_DEFAULT_CREATE_VALUES,
} from 'wb-spotify/app/routes/superGrid/mutator/utils';
import { SPOTIFY_SUPERGRID_CONFIG, transformSpotifyRows } from 'wb-spotify/app/routes/superGrid/config';
import { useTranslation } from 'react-i18next';
import {
	SpotifyAdAccountAddEntityDocument,
	SpotifyCampaignAddEntityDocument,
	SpotifyGroupAddEntityDocument,
} from 'wb-spotify/graphql/generated';
import { SpotifyLevel } from 'wb-spotify/app/routes/superGrid/utils';
import { useNavigate, useParams } from 'react-router-dom';
import { processParent } from 'wb-spotify/app/routes/superGrid/helpers';

const AddNewSpotifyEntityButton = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { params } = useUrlParams({
		navigate,
		useParams,
	});
	const { addMutation, mutations } = useSupergridMutations();

	const { data: adAccountData } = useQuery(SpotifyAdAccountAddEntityDocument, {
		variables: { filters: params.filters ?? {} },
		skip:      params.level !== SpotifyLevel.CAMPAIGN,
	});

	const { data: campaignData } = useQuery(SpotifyCampaignAddEntityDocument, {
		variables: { filters: params.filters ?? {} },
		skip:      params.level !== SpotifyLevel.GROUP,
	});

	const { data: groupData } = useQuery(SpotifyGroupAddEntityDocument, {
		variables: { filters: params.filters ?? {} },
		skip:      params.level !== SpotifyLevel.AD,
	});

	const hasSingleParent =	adAccountData?.entities?.items?.length === 1
		|| campaignData?.entities?.items?.length === 1
		|| groupData?.entities?.items?.length === 1;

	const parentEntity = addSupergridPropsToData(adAccountData?.entities?.items ?? [], SpotifyLevel.AD_ACCOUNT)?.[0]
		?? addSupergridPropsToData(campaignData?.entities?.items ?? [], SpotifyLevel.CAMPAIGN)?.[0]
		?? addSupergridPropsToData(groupData?.entities?.items ?? [], SpotifyLevel.GROUP)?.[0];

	const addEntity = async () => {
		let currLevelDepth = getLevelDepth(SPOTIFY_SUPERGRID_CONFIG, params.level);
		let creatingLevel = getLevelByDepth(SPOTIFY_SUPERGRID_CONFIG, currLevelDepth);
		let currParent = processParent(parentEntity);

		while (creatingLevel !== SpotifyLevel.BREAKDOWN) {
			const newMutation = {
				...(SPOTIFY_DEFAULT_CREATE_VALUES[creatingLevel]?.(currParent) ?? {}),
				name:
					`New ${startCase(creatingLevel)} ${(mutations?.[creatingLevel as SpotifyLevel]?.length ?? 0) + 1}`,
				currency: currParent?.currency,
				// this loop will execute at most 3 times so await inside is OK
				// eslint-disable-next-line no-await-in-loop
				parent:   (await transformSpotifyRows(
					[currParent],
					getSpotifyParentLevel(creatingLevel as SpotifyLevel),
				))?.[0] ?? {},
				hash:               generateMutationHash(),
				mutationInsertedAt: Date.now(),
			};

			addMutation(creatingLevel, newMutation);

			currParent = newMutation;
			creatingLevel = getLevelByDepth(SPOTIFY_SUPERGRID_CONFIG, ++currLevelDepth);
		}
	};

	return (
		<Button
			title={t('sc: new', 'New')}
			onClick={addEntity}
			leftNode={<PlusIcon width={16} />}
			disabled={!hasSingleParent}
			className="!pl-[6px] ml-[2px]"
			data-testid="add-new-entity-button"
		/>
	);
};

export default AddNewSpotifyEntityButton;
